import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, CircleAlert, CircleCheckBig, CircleX, Download, Eye, History, Link2, Pencil, Printer, Search, Undo2, X } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { DateInput, Select } from '../components/Controls';
import { Chip, Kpi, downloadXls, esc, iso, printDoc } from './attendance/shared';
import { t12 } from '../format';

// Time & Attendance > Attendance Management > Attendance Edit Log (Stephanet's AttendanceEditLog): every correction to an
// attendance record (status, hours, clock times) and its approval. Approve / Reject / Return run in the approval engine
// (ATTENDANCE_CORRECTION); rejecting puts the record back as it was. Codes: P present · PA partial · OT overtime ·
// A absent · L leave · H holiday · W weekend.

const CODE_CLS = { P: 'p', A: 'a', PA: 'pa', L: 'l', OT: 'ot', H: 'h', W: 'w' };
const CODE_NAME = { P: 'Present', A: 'Absent', PA: 'Partial', L: 'Leave', OT: 'Overtime', H: 'Holiday', W: 'Weekend' };
const APPR_TONE = { Approved: 'green', Pending: 'yellow', Rejected: 'red', Returned: 'orange' };
const PAGE = 10;
const hm = v => { if (v === null || v === undefined || v === '') return '0:00'; const n = Number(v); const h = Math.floor(n); return `${h}:${String(Math.round((n - h) * 60)).padStart(2, '0')}`; };
const dt = s => (s ? s.replace('T', ' ').slice(0, 16) : '-');
const EMPTY = () => { const t = new Date(); return { start: iso(new Date(t.getFullYear(), t.getMonth() - 2, 1)), end: iso(t), approval: '', department: '', site: '' }; };
const Code = ({ c }) => (c ? <span className={`ael-code ar-${CODE_CLS[c] || 'none'}`} title={CODE_NAME[c]}>{c}</span> : '—');

export default function AttendanceEditLog() {
  const { notify } = useHR();
  const [f, setF] = useState(EMPTY);
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [sq, setSq] = useState('');   // search sent to the server (debounced) so KPIs, export and paging follow it
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deciding, setDeciding] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setSq(q.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  const load = useCallback(() => {
    if (f.start && f.end && f.start > f.end) { setError('From Date cannot be later than To Date.'); return; }
    hr.getEditLog({ ...f, q: sq }).then(r => { setD(r); setError(''); }).catch(e => setError(e.message));
  }, [f, sq]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setF(s => ({ ...s, [k]: v })); setPage(1); };
  const reset = () => { setF(EMPTY()); setQ(''); setSq(''); setPage(1); };
  const base = EMPTY();
  const filtered = f.approval || f.department || f.site || q || f.start !== base.start || f.end !== base.end;

  const rows = useMemo(() => (d ? d.rows : []), [d]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading attendance edit log…" subtitle="Fetching corrections" />;
  const k = d.kpis;
  const OPEN = ['Pending'];

  const exportXls = () => {
    const head = ['S/N', 'Reference', 'Employee ID', 'Employee Name', 'Department', 'Project Site', 'Att. Date', 'Old Status', 'New Status', 'Old Hours', 'New Hours',
      'Old Time In', 'New Time In', 'Old Time Out', 'New Time Out', 'Edited By', 'Date Edited', 'Reason', 'Approval Status', 'Approved By', 'Approval Date', 'Approval Remark', 'IP Address'];
    const nm = (list, id) => list.find(x => String(x.id) === String(id))?.name;
    const body = `<p>From Date: ${d.start} · To Date: ${d.end} · Approval: ${f.approval || 'All Status'} · Department: ${nm(d.filters.departments, f.department) || 'All Departments'} · Project Site: ${nm(d.filters.sites, f.site) || 'All Sites'}${sq ? ` · Search: ${esc(sq)}` : ''}</p>
      <table border="1"><thead><tr>${head.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map((r, i) => `<tr>${[i + 1, r.reference, r.employee_no, r.name, r.department, r.site, r.date,
      r.old_status, r.new_status, hm(r.old_hours), hm(r.new_hours), t12(r.old_time_in) || '-', t12(r.new_time_in) || '-', t12(r.old_time_out) || '-', t12(r.new_time_out) || '-', r.edited_by,
      dt(r.edited_at), r.reason, r.approval, r.approved_by || '-', dt(r.approval_date), r.approval_remark || '', r.ip || ''].map(c => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
    downloadXls(`Attendance_Edit_Log_${d.start}_to_${d.end}.xls`, 'Attendance Edit Log', body);
    notify('Attendance edit log exported');
  };
  const tryEdit = r => {
    if (['Approved', 'Rejected'].includes(r.approval)) { notify(`This correction is ${r.approval.toLowerCase()} and can no longer be changed.`, 'bad'); return; }
    setEditing(r);
  };

  return (
    <div className="mdr mds ael">
      <div className="mdr-top">
        <div>
          <h2>Attendance Edit Log</h2>
          <p>Track all edited attendance records, approval changes, time corrections, and audit history.</p>
        </div>
      </div>

      <div className="md-kpis mds-kpis ts-kpis ael-kpis">
        <Kpi label="Total Corrections" value={k.total} Ico={History} tone="blue" note={`${d.start} to ${d.end}`} />
        <Kpi label="Approved" value={k.approved} Ico={CircleCheckBig} tone="green" note="Confirmed" i={1} />
        <Kpi label="Pending" value={k.pending} Ico={CircleAlert} tone="amber" note="Awaiting approval" i={2} />
        <Kpi label="Rejected / Returned" value={k.rejected + k.returned} Ico={CircleX} tone="red" note={`${k.rejected} rejected · ${k.returned} returned`} i={3} />
      </div>

      <div className="mdr-card ar-filters no-print">
        <div className="ar-grid ael-grid">
          <label>From Date<DateInput value={f.start} onChange={e => set('start', e.target.value)} /></label>
          <label>To Date<DateInput value={f.end} onChange={e => set('end', e.target.value)} /></label>
          <label>Approval Status<Select value={f.approval} onChange={e => set('approval', e.target.value)}><option value="">All Status</option>{d.approval.map(x => <option key={x} value={x}>{x}</option>)}</Select></label>
          <label>Department<Select value={f.department} onChange={e => set('department', e.target.value)}><option value="">All Departments</option>{d.filters.departments.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></label>
          <label>Project Site<Select value={f.site} onChange={e => set('site', e.target.value)}><option value="">All Sites</option>{d.filters.sites.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></label>
        </div>
        <div className="ar-actions">
          <div className="mdr-search ael-search"><Search size={14} /><input placeholder="Search name, ID, department, site, editor or reason..." value={q} onChange={e => setQ(e.target.value)} />
            {q && <button className="ael-clear" onClick={() => setQ('')} title="Clear search"><X size={13} /></button>}</div>
          {filtered && <button className="mdr-btn" onClick={reset}><Undo2 size={14} /> Reset Filters</button>}
          {d.can.export && <button className="mdr-btn" disabled={!rows.length} onClick={exportXls}><Download size={14} /> Export Excel</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
        {error && <div className="ar-error">{error}</div>}
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table ael-table">
            <thead><tr>
              <th className="c no-print">Action</th><th className="c">S/N</th><th>Employee ID</th><th>Employee Name</th><th>Att. Date</th><th className="c">Old Status</th><th className="c">New Status</th>
              <th className="c">Old Hours</th><th className="c">New Hours</th><th>Edited By</th><th>Date Edited</th><th>Reason</th><th>Approval Status</th><th>Approved By</th>
            </tr></thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  <td className="c no-print nowrap">
                    <button className="mds-view" onClick={() => setViewing(r.id)} title="View Details"><Eye size={12} /></button>
                    {d.can.edit && <button className="mds-view ael-edit" onClick={() => tryEdit(r)} title="Edit Record"><Pencil size={12} /></button>}
                    {d.can.approve && OPEN.includes(r.approval) && <>
                      <button className="mds-view ael-ok" onClick={() => setDeciding({ r, action: 'approve' })} title="Approve"><Check size={12} /></button>
                      <button className="mds-view ael-no" onClick={() => setDeciding({ r, action: 'reject' })} title="Reject (restores the record)"><X size={12} /></button>
                    </>}
                  </td>
                  <td className="c">{(page - 1) * PAGE + i + 1}</td>
                  <td className="mono">{r.employee_no}</td><td className="nowrap"><b>{r.name}</b></td><td className="nowrap">{r.date}</td>
                  <td className="c"><Code c={r.old_status} /></td><td className="c"><Code c={r.new_status} /></td>
                  <td className="c mono">{hm(r.old_hours)}</td><td className="c mono">{hm(r.new_hours)}</td>
                  <td className="nowrap">{r.edited_by}</td><td className="nowrap small">{dt(r.edited_at)}</td>
                  <td className="ael-reason" title={r.reason}>{r.reason}</td>
                  <td><Chip tone={APPR_TONE[r.approval]}>{r.approval}</Chip></td><td className="small">{r.approved_by || '-'}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={14} className="mdr-empty">{sq ? `No attendance corrections match "${sq}" for the selected filters.` : 'No attendance corrections for the selected criteria. Corrections appear here when a saved attendance record is changed.'}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="ael-pager">
          <span>Showing {rows.length ? (page - 1) * PAGE + 1 : 0} to {Math.min(page * PAGE, rows.length)} of {rows.length} records</span>
          <div>
            <button className="mdr-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
            <button className="mdr-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {viewing && <DetailModal id={viewing} notify={notify} canApprove={d.can.approve} onDecide={(r, action) => { setViewing(null); setDeciding({ r, action }); }} onClose={() => setViewing(null)} />}
      {deciding && <DecideModal {...deciding} notify={notify} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); load(); }} />}
      {editing && <EditModal r={editing} notify={notify} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function DetailModal({ id, notify, canApprove, onDecide, onClose }) {
  const [x, setX] = useState(null);
  useEffect(() => { hr.getCorrection(id).then(setX).catch(e => notify(e.message, 'bad')); }, [id, notify]);
  if (!x) return <Modal title="Attendance Edit Log Details" onClose={onClose}><Loader title="Loading…" subtitle="Fetching the record" /></Modal>;
  const groups = [
    ['Employee Information', [['Employee ID', x.employee_no], ['Department', x.department], ['Employee Name', x.name], ['Project Site', x.site]]],
    ['Attendance Edit Summary', [['Reference', x.reference], ['Attendance Date', x.date], ['Old Status', <Code key="o" c={x.old_status} />], ['New Status', <Code key="n" c={x.new_status} />],
      ['Old Hours', hm(x.old_hours)], ['New Hours', hm(x.new_hours)], ['Old OT Hours', hm(x.old_ot)], ['New OT Hours', hm(x.new_ot)],
      ['Old Time In', t12(x.old_time_in) || '-'], ['New Time In', t12(x.new_time_in) || '-'], ['Old Time Out', t12(x.old_time_out) || '-'], ['New Time Out', t12(x.new_time_out) || '-']]],
    ['Edit Reason and Notes', [['Reason', x.reason], ['Edited By', x.edited_by], ['Date Edited', dt(x.edited_at)], ['Device/Terminal', x.device], ['IP Address', x.ip || '-']]],
    ['Approval Information', [['Approval Status', <Chip key="a" tone={APPR_TONE[x.approval]}>{x.approval}</Chip>], ['Approved By', x.approved_by || '-'],
      ['Approval Date', dt(x.approval_date)], ['Approval Remark', x.approval_remark || '-']]],
  ];
  const plain = { 'Old Status': x.old_status, 'New Status': x.new_status, 'Approval Status': x.approval };
  const print = () => printDoc(`Attendance Correction — ${x.name} (${x.date})`, groups.flatMap(([, items]) => items.map(([k, v]) => [k, plain[k] ?? v])),
    `${x.employee_no} · ${x.department || ''} · ${x.site || ''}`);
  return (
    <Modal wide title="Attendance Edit Log Details" onClose={onClose}>
      <div className="ael-detail">
        {groups.map(([title, items]) => (
          <section key={title}><h4 className="wh-sub">{title}</h4>
            <div className="ael-kv">{items.map(([k, v]) => <div key={k} className={k === 'Device/Terminal' ? 'wide' : ''}><small>{k}</small><span>{v || '—'}</span></div>)}</div></section>
        ))}
        {x.changes?.length > 0 && (
          <section><h4 className="wh-sub">Fields Changed</h4>
            <div className="ael-kv">{x.changes.map(c => <div key={c.field}><small>{c.field}</small><span>{c.old ?? '—'} → {c.new ?? '—'}</span></div>)}</div></section>
        )}
        <section className="ael-linked"><h4 className="wh-sub"><Link2 size={13} /> Linked Updates</h4>
          {x.impact?.length ? x.impact.map((s, i) => (
            <div key={i} className="ael-impact"><div className="ael-impact-h"><b>{s.stage}</b><small>{dt(s.at)}</small></div>
              {s.lines.length ? <ul>{s.lines.map((l, j) => { const [mod, ...rest] = l.split(': '); return <li key={j}>{rest.length ? <><span className="ael-mod">{mod}</span>{rest.join(': ')}</> : mod}</li>; })}</ul>
                : <div className="muted small">No other module was affected.</div>}</div>
          )) : <div className="muted small">No linked updates recorded for this correction.</div>}
        </section>
        {x.approval_steps?.length > 0 && (
          <section><h4 className="wh-sub">Approval Steps</h4>
            <div className="ael-kv">{x.approval_steps.map(s => <div key={s.step}><small>{s.step}</small><span>{s.status}{s.by ? ` · ${s.by}` : ''}{s.at ? ` · ${dt(s.at)}` : ''}{s.comment ? ` — ${s.comment}` : ''}</span></div>)}</div></section>
        )}
      </div>
      <div className="mdh-form-foot">
        {canApprove && x.approval === 'Pending' && <>
          <button className="mdr-btn ael-no-btn" onClick={() => onDecide(x, 'reject')}><X size={13} /> Reject</button>
          <button className="mdr-btn" onClick={() => onDecide(x, 'return')}><Undo2 size={13} /> Return</button>
          <button className="mds-run" onClick={() => onDecide(x, 'approve')}><Check size={13} /> Approve</button>
        </>}
        <button className="mdr-btn" onClick={print}><Printer size={13} /> Print Details</button><button className="mds-run" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

function EditModal({ r, notify, onClose, onSaved }) {
  const [x, setX] = useState(null);
  const [f, setF] = useState({ new_status: r.new_status, new_hours: r.new_hours ?? '', reason: r.reason || '' });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { hr.getCorrection(r.id).then(setX).catch(e => notify(e.message, 'bad')); }, [r.id, notify]);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const validate = () => {
    const e = {};
    if (!f.new_status) e.new_status = 'New Status is required';
    if (!f.reason.trim()) e.reason = 'Reason is required';
    if (String(f.new_hours).includes('-')) e.new_hours = 'Hours cannot be negative';
    setErr(e);
    return !Object.keys(e).length;
  };
  const save = () => {
    if (!validate()) return;
    setBusy(true);
    hr.updateCorrection(r.id, { new_status: f.new_status, new_hours: f.new_hours === '' || timed ? null : Number(f.new_hours), reason: f.reason.trim() })
      .then(res => { notify(res.message); onSaved(); }).catch(e => notify(e.message, 'bad')).finally(() => setBusy(false));
  };
  const timed = x?.has_clock_times;
  const E = ({ k }) => (err[k] ? <small className="ael-err">{err[k]}</small> : null);
  return (
    <Modal className="wh-modal" title="Edit Attendance Correction Record" onClose={onClose}>
      <div className="mdr-form wh-form">
        <div className="lvm-grid two">
          <label>Employee ID (Read-only)<input value={`${r.employee_no} — ${r.name}`} disabled /></label>
          <label>Attendance Date (Read-only)<input value={r.date} disabled /></label>
          <label>Old Status (Read-only)<input value={`${r.old_status} — ${CODE_NAME[r.old_status] || ''}`} disabled /></label>
          <label><span>New Status {err.new_status && <b className="wh-req">*</b>}</span>
            <Select value={f.new_status} onChange={e => set('new_status', e.target.value)}>{['P', 'A', 'PA', 'L', 'OT'].map(c => <option key={c} value={c}>{c} — {CODE_NAME[c]}</option>)}</Select><E k="new_status" /></label>
          <label>Old Hours (Read-only)<input value={hm(r.old_hours)} disabled /></label>
          <label><span>New Hours {err.new_hours && <b className="wh-req">*</b>}</span>
            <input type="number" step="0.25" min="0" max="24" value={f.new_hours} disabled={timed || ['A', 'L'].includes(f.new_status)} onChange={e => set('new_hours', e.target.value)} />
            {timed ? <small className="muted">Hours follow the clock times on the record ({t12(x.new_time_in)}–{t12(x.new_time_out)}).</small> : <E k="new_hours" />}</label>
        </div>
        <label className="wh-block"><span>Reason {err.reason && <b className="wh-req">*</b>}</span><textarea rows={2} value={f.reason} placeholder="Edit reason" onChange={e => set('reason', e.target.value)} /><E k="reason" /></label>
        <p className="muted small">
          {r.approval === 'Returned' ? 'This correction was returned — saving sends it back for approval.' : 'Approve, reject or return the correction with the buttons on the log; rejecting puts the attendance record back as it was.'}
        </p>
        <div className="mdh-form-foot wh-form-foot">
          <button className="mdr-btn" onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy} onClick={save}><CircleCheckBig size={14} /> {busy ? 'Saving…' : r.approval === 'Returned' ? 'Save & Resubmit' : 'Save Changes'}</button>
        </div>
      </div>
    </Modal>
  );
}

const DECIDE = {
  approve: { title: 'Approve Attendance Correction', btn: 'Approve', note: 'The correction stays on the attendance record and is confirmed.' },
  reject: { title: 'Reject Attendance Correction', btn: 'Reject', note: 'The attendance record is put back as it was before the correction.' },
  return: { title: 'Return Attendance Correction', btn: 'Return', note: 'Sent back for checking. The attendance record is not changed.' },
};

export function DecideModal({ r, action, notify, onClose, onDone }) {
  const [remark, setRemark] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const m = DECIDE[action];
  const go = async () => {
    if (action !== 'approve' && !remark.trim()) { setErr(action === 'reject' ? 'Rejection reason is required' : 'Say why it is being returned'); return; }
    setBusy(true);
    try {
      const res = await hr.correctionAction(r.id, { action, remark: remark.trim() || null });
      notify(res.message); onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={m.title} onClose={onClose}>
      <div className="ael-decide">
        <div className="ael-decide-sum"><b>{r.name}</b> · {r.date} · <Code c={r.old_status} /> → <Code c={r.new_status} /> · {hm(r.old_hours)} → {hm(r.new_hours)}
          <div className="muted small">{r.reason}</div></div>
        <p className="small">{m.note}</p>
        <label className="ael-field">{action === 'approve' ? 'Approval Remark (optional)' : action === 'reject' ? 'Rejection Reason *' : 'Reason for Returning *'}
          <textarea rows={3} value={remark} onChange={e => { setRemark(e.target.value); setErr(''); }} className={err ? 'bad' : ''} /></label>
        {err && <div className="ael-err">{err}</div>}
      </div>
      <div className="mdh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className={`mds-run ${action === 'reject' ? 'ael-danger' : ''}`} disabled={busy} onClick={go}>{busy ? 'Saving…' : m.btn}</button></div>
    </Modal>
  );
}
